// src/components/Sidebar.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Box,
  IconButton,
  ListItemButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../assets/images/Logo-NW175203.png';
import { LuArrowLeftToLine, LuArrowRightToLine } from 'react-icons/lu';
import { getSidebarIcon } from '../icons/sidebarIcons';
import { AuthContext } from '../contexts/AuthContext'; // موجود عندك (بدون useAuth)
import './../App.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PERMISSIONS_UPDATED_EVENT = 'smart-permissions-updated';

function notifyPermissionsUpdated() {
  window.dispatchEvent(new CustomEvent(PERMISSIONS_UPDATED_EVENT));
}

const iconFor = (itemOrKey) => {
  const Icon = getSidebarIcon(itemOrKey);
  return <Icon />;
};

async function fetchJSON(url, options = {}) {
  const token = localStorage.getItem('token') || '';
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    try {
      const j = await res.json();
      throw new Error(j?.message || j?.error || `HTTP ${res.status}`);
    } catch {
      throw new Error(`HTTP ${res.status}`);
    }
  }
  return res.json();
}

// === يبني قائمة افتراضية من permissions في localStorage (fallback) ===
function buildMenuFromPermissions(permissions = []) {
  const PERM_TO_MENU = {
    home: { path: '/home', icon_key: 'home', text_key: 'home' },
    statistics: {
      path: '/statistics',
      icon_key: 'statistics',
      text_key: 'statistics',
    },
    horses: { path: '/horse-list', text_key: 'horse-list' },
    competitions: {
      path: '/competitions',
      icon_key: 'competitions',
      text_key: 'competitions',
    },
    accommodation: {
      path: '/available-rooms',
      icon_key: 'available-rooms',
      text_key: 'available-rooms',
    },
    vetRecords: { path: '/RecordList', text_key: 'recordlist' },
    farrierRecord: { path: '/FarrierList', text_key: 'farrierlist' },
    farrierlist: { path: '/FarrierList', text_key: 'farrierlist' },
    'farrier-list': { path: '/FarrierList', text_key: 'farrierlist' },
    reproductionpage: {
      path: '/ReproductionPage',
      text_key: 'reproductionpage',
    },
    horsetransportreportpage: {
      path: '/horsetransportreportpage',
      icon_key: 'HorseTransportReportPage',
      text_key: 'horsetransportreportpage',
    },
    accountantreview: {
      path: '/accountantreview',
      text_key: 'accountantreview',
    },
    checkup: {
      path: '/vetcheckupview',
      icon_key: 'checkup',
      text_key: 'checkup',
    },
    settings: { path: '/settings', icon_key: 'settings', text_key: 'settings' },
    'my-horses': {
      path: '/my-horses',
      icon_key: 'horses',
      text_key: 'client_horses',
    },
    myhorses: {
      path: '/my-horses',
      icon_key: 'horses',
      text_key: 'client_horses',
    },
    'my-horse-stats': {
      path: '/my-horse-stats',
      icon_key: 'statistics',
      text_key: 'client_horse_stats',
    },
    myhorsestats: {
      path: '/my-horse-stats',
      icon_key: 'statistics',
      text_key: 'client_horse_stats',
    },
  };

  const out = [];
  const seen = new Set();
  for (const p of permissions || []) {
    if (Number(p?.can_access) !== 1) continue;
    const key = String(p.page_name || p.permission_key || '').toLowerCase();
    const cfg = PERM_TO_MENU[key];
    if (!cfg) continue;
    if (seen.has(cfg.path)) continue;
    seen.add(cfg.path);
    out.push({
      permission_key: key,
      text_key: cfg.text_key || key,
      icon_key: cfg.icon_key || key,
      path: cfg.path,
    });
  }
  if (!out.some((i) => i.path === '/home')) {
    out.unshift({
      permission_key: 'home',
      text_key: 'home',
      icon_key: 'home',
      path: '/home',
    });
  }
  return out;
}

const isHorseOwnerAccount = () => {
  if (Number(localStorage.getItem('role_id') || 0) !== 3) return false;
  const ownerId = localStorage.getItem('owner_id');
  return Boolean(ownerId && ownerId !== 'null' && ownerId !== 'undefined');
};

const normalizeMenuPath = (value = '') =>
  ('/' + String(value || '').trim())
    .replace(/^\/+/, '/')
    .replace(/\/+/g, '/')
    .toLowerCase();

const LEGACY_ACCESS_PATHS = new Set([
  '/dashboard',
  '/clientslist',
  '/horsesclientslist',
  '/stable-details',
  '/room-details',
  '/stables/add',
  '/vetclinicrecordinside',
  '/vetclinicrecordoutside',
  '/reproduction-details',
  '/directinseminationform',
  '/addfarrierrecordinside',
  '/addfarrierrecordoutside',
  '/farrier-medications',
  '/farrier-medication-add',
  '/farrier-medication-edit',
  '/expensespage',
  '/horsefinancecenter',
  '/rolelist',
  '/roles-permissions',
  '/role-permissions',
  '/admin/menu-designer',
  '/userpermissions',
]);

const SETTINGS_CHILD_PATHS = new Set([
  '/access-control',
  '/settings/access-control',
  '/settings/audit-report',
  '/settings/backups',
  '/settings/pricing',
]);

const orderAccessControlAfterSettings = (items = []) => {
  const accessItems = [];
  const others = [];

  (items || []).forEach((item) => {
    const path = normalizeMenuPath(item.path || item.route);
    if (path === '/access-control') accessItems.push(item);
    else others.push(item);
  });

  if (!accessItems.length) return others;

  const settingsIndex = others.findIndex((item) => {
    const path = normalizeMenuPath(item.path || item.route);
    const key = String(item.permission_key || item.text_key || '').toLowerCase();
    return path === '/settings' || key.includes('settings');
  });

  if (settingsIndex >= 0) {
    others.splice(settingsIndex + 1, 0, ...accessItems);
    return others;
  }

  return [...others, ...accessItems];
};

const prepareMenuItems = (items = []) => {
  const visible = (items || []).filter((item) => {
    const path = normalizeMenuPath(item.path || item.route);
    return !LEGACY_ACCESS_PATHS.has(path) && !SETTINGS_CHILD_PATHS.has(path);
  });

  return orderAccessControlAfterSettings(visible);
};

const filterMenuForOwnerAccount = (items = []) => {
  if (!isHorseOwnerAccount()) return items;
  const ownerDefaultItems = [
    { permission_key: 'home', text_key: 'home', icon_key: 'home', path: '/home' },
  ];

  const byPath = new Map();
  [...ownerDefaultItems, ...(items || [])].forEach((item) => {
    const path = normalizeMenuPath(item.path || item.route);
    if (!path) return;
    byPath.set(path, item);
  });

  return [...byPath.values()];
};

// === استخراج أدوار المستخدم من الـ Context أو من localStorage ===
function useRoleIds(authValue) {
  return useMemo(() => {
    // 1) حاول من الـ Context
    if (authValue?.user) {
      const u = authValue.user;
      if (Array.isArray(u.roles) && u.roles.length) {
        return u.roles.map((r) => Number(r.role_id)).filter(Boolean);
      }
      if (u.role_id) return [Number(u.role_id)];
    }
    // 2) حاول من localStorage
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      if (user?.roles?.length)
        return user.roles.map((r) => Number(r.role_id)).filter(Boolean);
      if (user?.role_id) return [Number(user.role_id)];
    } catch {}
    const lsRole = Number(localStorage.getItem('role_id') || 0);
    if (lsRole) return [lsRole];

    // 3) افتراضي: مدير الإسطبل (1)
    return [1];
  }, [authValue?.user]);
}

const Sidebar = ({
  isAuthenticated,
  setIsAuthenticated,
  expanded,
  toggleExpand,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authValue = useContext(AuthContext);

  const roleIds = useRoleIds(authValue);
  const roleKey = roleIds.join(',');

  const sidebarWidth = expanded ? 200 : 60;
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuRefreshTick, setMenuRefreshTick] = useState(0);

  useEffect(() => {
    const refresh = () => setMenuRefreshTick((value) => value + 1);
    window.addEventListener('smart-menu-refresh', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('smart-menu-refresh', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  // تحميل القائمة (app-menu) وصلاحيات الدور/القائمة (role-menu)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);

        /*
        let cached = [];
        try {
          cached = JSON.parse(localStorage.getItem('me_menu') || '[]');
        } catch {}*/
        // سنعتمد على الباكند؛ لكن لو فشل كل شيء نرجع للـ cached/fallback

        const [allMenu, roleMenu, permissionsRes, myMenuRes] = await Promise.all([
          fetchJSON(`${API_BASE}/app-menu`), // مصفوفة عناصر القائمة
          fetchJSON(`${API_BASE}/role-menu`), // [{role_id, menu_id, allowed}, ...]
          fetchJSON(`${API_BASE}/me/permissions`).catch(() => null),
          fetchJSON(`${API_BASE}/me/menu`).catch(() => null),
        ]);

        if (cancelled) return;
        if (permissionsRes?.permissions) {
          const permissionsData = Object.entries(permissionsRes.permissions).map(([page_name, allowed]) => ({
            page_name,
            can_access: allowed ? 1 : 0,
          }));
          try {
            localStorage.setItem('permissions', JSON.stringify(permissionsData));
          } catch {}
        }

        const activeMenu = (Array.isArray(allMenu) ? allMenu : []).filter(
          (m) => Number(m.is_active) === 1
        );
        try {
          localStorage.setItem('app_menu_all', JSON.stringify(Array.isArray(allMenu) ? allMenu : []));
        } catch {}

        // IDs المسموحة بناءً على أدوار المستخدم
        const allowed = new Set();
        if (Array.isArray(roleMenu)) {
          for (const row of roleMenu) {
            if (
              roleIds.includes(Number(row.role_id)) &&
              Number(row.allowed) === 1
            ) {
              allowed.add(Number(row.menu_id));
            }
          }
        }

        // لو لم يصل roleMenu لأي سبب، لا نفلتر بالصلاحيات (fallback): نعرض activeMenu فقط
        const hasRoleMenuRows = Array.isArray(roleMenu) && roleMenu.some((row) =>
          roleIds.includes(Number(row.role_id))
        );
        const userMenu = Array.isArray(myMenuRes?.items)
          ? myMenuRes.items.filter((m) => Number(m.is_active) === 1)
          : [];

        const filtered = userMenu.length
          ? userMenu
          : hasRoleMenuRows
            ? activeMenu.filter((m) => allowed.has(Number(m.menu_id)))
            : buildMenuFromPermissions(permissionsRes?.permissions
                ? Object.entries(permissionsRes.permissions).map(([page_name, allowedPage]) => ({
                    page_name,
                    can_access: allowedPage ? 1 : 0,
                  }))
                : []);

        const sorted = orderAccessControlAfterSettings(
          filterMenuForOwnerAccount(prepareMenuItems(filtered)).sort(
            (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
          )
        );
        setMenuItems(sorted);

        // خزّن نسخة لـ Sidebar سريع لاحقاً
        try {
          localStorage.setItem('me_menu', JSON.stringify(sorted));
        } catch {}
        notifyPermissionsUpdated();
      } catch (e) {
        console.warn('Sidebar: falling back to local permissions:', e.message);

        // فشل النداء → ابنِ القائمة من permissions المحلي
        let perms = [];
        try {
          perms = JSON.parse(localStorage.getItem('permissions') || '[]');
        } catch {}
        const built = orderAccessControlAfterSettings(filterMenuForOwnerAccount(prepareMenuItems(buildMenuFromPermissions(perms))));
        setMenuItems(built);
        try {
          localStorage.setItem('me_menu', JSON.stringify(built));
        } catch {}
        notifyPermissionsUpdated();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // نعتمد على الأدوار فقط. API_BASE ثابت ولا يحتاج dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey, menuRefreshTick]);

  return (
    <Drawer
      anchor="left"
      open={true}
      variant="persistent"
      PaperProps={{ dir: 'ltr' }}
      sx={{
        '& .MuiPaper-root': {
          position: 'fixed',
          top: 0,
          left: 0,
          height: '98vh',
          width: sidebarWidth,
          backgroundColor: '#252248',
          color: '#FFFFFF',
          margin: '10px',
          transition: 'width 0.3s ease',
          boxShadow: 'none',
          borderRadius: '13px',
          overflow: 'hidden',
          zIndex: 1300,
          direction: 'ltr',
        },
      }}
    >
      <Box
        dir="ltr"
        sx={{
          backgroundColor: '#252248',
          color: '#FFFFFF',
          height: '100%',
          padding: '5px',
          borderRadius: '13px',
          overflow: 'hidden',
        }}
        role="presentation"
      >
        {/* الشعار + زر التوسيع */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: expanded ? 'space-between' : 'center',
            paddingTop: '10px',
            direction: 'ltr',
          }}
        >
          {expanded && (
            <img
              src={Logo}
              alt="Smart Stable"
              style={{ height: '30px', margin: '10px 5px 10px 30px' }}
            />
          )}
          <IconButton
            onClick={toggleExpand}
            sx={{
              color: '#d5c6a9',
              mx: expanded ? 0 : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {expanded ? <LuArrowLeftToLine /> : <LuArrowRightToLine />}
          </IconButton>
        </Box>

        <Divider sx={{ backgroundColor: '#d5c6a9' }} />

        {/* قائمة العناصر */}
        <List sx={{ direction: 'ltr' }}>
          {menuItems.map((item) => (
            <ListItem
              disablePadding
              key={item.menu_id || item.permission_key || item.text_key}
            >
              <ListItemButton
                onClick={() => navigate(item.path || item.route || '/')}
                sx={{
                  flexDirection: 'row',
                  gap: expanded ? 1.2 : 0,
                  minHeight: 46,
                  justifyContent: expanded ? 'flex-start' : 'center',
                  px: expanded ? 2 : 0,
                  direction: 'ltr',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: expanded ? '30px' : 0,
                    width: expanded ? 30 : '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#d5c6a9', // اللون الموحد
                    '& svg': { width: 20, height: 20, display: 'block' },
                    // اجبر كل العناصر داخل svg على استخدام currentColor
                    '& svg [fill]:not([fill="none"])': {
                      fill: 'currentColor !important',
                    },
                    '& svg [stroke]:not([stroke="none"])': {
                      stroke: 'currentColor !important',
                    },
                  }}
                >
                  {iconFor(item)}
                </ListItemIcon>
                {expanded && (
                  <ListItemText
                    primary={t(item.text_key || item.permission_key || '')}
                    primaryTypographyProps={{
                      dir: 'auto',
                      textAlign: 'left',
                      fontWeight: 600,
                    }}
                    sx={{ m: 0 }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
          {!loading && menuItems.length === 0 && (
            <Box sx={{ px: 2, py: 1, opacity: 0.7, fontSize: 12 }}>
              {t('لا توجد عناصر متاحة لهذا الدور.')}
            </Box>
          )}
        </List>

        <Divider sx={{ backgroundColor: '#d5c6a9', my: 1 }} />
      </Box>
    </Drawer>
  );
};

export default Sidebar;
